import React, { useState, useEffect } from 'react';
import { DynamicIcon } from './icons/DynamicIcon';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  necessary: boolean;
  performance: boolean;
  functional: boolean;
  marketing: boolean;
}

const CookieManager: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    performance: false,
    functional: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already set preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (!savedPreferences) {
      // Show banner if no preferences are saved
      setShowBanner(true);
    } else {
      // Apply saved preferences
      setPreferences(JSON.parse(savedPreferences));
      applyCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      performance: true,
      functional: true,
      marketing: true
    };
    
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      performance: false,
      functional: false,
      marketing: false
    };
    
    setPreferences(necessaryOnly);
    savePreferences(necessaryOnly);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    applyCookiePreferences(prefs);
  };

  const applyCookiePreferences = (prefs: CookiePreferences) => {
    // Google Analytics
    if (prefs.performance) {
      if (window.gtag) {
        window.gtag('config', 'GA_MEASUREMENT_ID');
        window.gtag('consent', 'update', {
          analytics_storage: 'granted'
        });
      }
    } else {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied'
        });
      }
    }

    // Facebook Pixel
    if (prefs.marketing) {
      if (window.fbq) {
        window.fbq('init', 'YOUR_PIXEL_ID');
        window.fbq('track', 'PageView');
      }
    }

    // Hotjar
    if (prefs.performance) {
      if (window.hj) {
        window.hj('event', 'consent_given');
      }
    }
  };

  const handleTogglePreference = (key: keyof CookiePreferences) => {
    // Don't allow toggling necessary cookies
    if (key === 'necessary') return;
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const openPreferences = () => {
    setShowPreferences(true);
    setShowBanner(false);
  };

  if (!showBanner && !showPreferences) {
    return (
      <button
        onClick={() => setShowPreferences(true)}
        className="fixed bottom-4 left-4 p-3 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors z-50"
        aria-label="Gerenciar cookies"
      >
        <DynamicIcon name="cookie" size={20} />
      </button>
    );
  }

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4 z-50 animate-fade-in">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nós valorizamos sua privacidade</h3>
                <p className="text-gray-600 text-sm">
                  Utilizamos cookies para melhorar sua experiência, personalizar conteúdo e analisar o tráfego. 
                  Você pode escolher quais cookies aceitar.
                  <a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 ml-1">Saiba mais</a>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={openPreferences}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Preferências
                </button>
                <button
                  onClick={acceptNecessary}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Apenas Necessários
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Aceitar Todos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Preferências de Cookies</h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <DynamicIcon name="x" size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <p className="text-gray-600 mb-6">
                Utilizamos cookies para melhorar sua experiência no EventPour. Você pode personalizar suas preferências abaixo.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Para mais informações sobre como utilizamos cookies, consulte nossa <a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Política de Cookies</a>.
              </p>
              
              <div className="space-y-6">
                {/* Necessary Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={preferences.necessary}
                        disabled
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-gray-900 font-medium">Cookies Necessários</label>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Obrigatório</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Cookies essenciais para o funcionamento básico do site. Eles permitem recursos como navegação e acesso a áreas seguras.
                  </p>
                </div>
                
                {/* Performance Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={preferences.performance}
                        onChange={() => handleTogglePreference('performance')}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-gray-900 font-medium">Cookies de Desempenho</label>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">Opcional</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Ajudam-nos a entender como os visitantes interagem com o site, coletando e relatando informações anonimamente.
                  </p>
                </div>
                
                {/* Functional Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => handleTogglePreference('functional')}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-gray-900 font-medium">Cookies Funcionais</label>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">Opcional</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Permitem que o site forneça funcionalidades e personalização aprimoradas, como lembrar suas preferências.
                  </p>
                </div>
                
                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handleTogglePreference('marketing')}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-gray-900 font-medium">Cookies de Marketing</label>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">Opcional</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Usados para rastrear visitantes em sites. A intenção é exibir anúncios relevantes e envolventes para o usuário.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end items-center p-6 border-t bg-gray-50 gap-3">
              <button
                onClick={() => setShowPreferences(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Salvar Preferências
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieManager;